import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pen, RotateCcw, Check, X, Undo2 } from "lucide-react";

/**
 * Campo firma.
 *
 * Nel modulo occupa poco: una striscia che mostra la firma gia' presa oppure
 * l'invito a firmare. Il disegno vero si fa a tutto schermo, perche' firmare
 * col dito dentro una striscia alta ottanta punti, per giunta dentro una
 * finestra che scorre, non funziona su un telefono in cantiere.
 *
 * Tre cose che sembrano dettagli e non lo sono:
 *
 *  - Il tratto si tiene come elenco di punti, non come pixel. Cosi' girando il
 *    telefono la firma si ridisegna invece di sparire, e "Annulla tratto" e'
 *    possibile.
 *  - La tela viene dimensionata sui pixel veri dello schermo (devicePixelRatio).
 *    Allargare via CSS una tela 300x150 e' il motivo per cui il segno usciva
 *    spostato rispetto al dito.
 *  - Si usano i Pointer Event, non i Touch Event: React registra touchstart e
 *    touchmove come passivi, quindi li' preventDefault non ha effetto e la
 *    pagina scorre mentre si firma.
 */
export default function CampoFirma({
  valore, onChange, etichetta = "Firma", descrizione, obbligatoria = false,
}) {
  const [aperto, setAperto] = useState(false);
  return (
    <div>
      <Label>{etichetta}{obbligatoria && " *"}</Label>
      {descrizione && <p className="text-xs text-slate-500 mt-0.5">{descrizione}</p>}

      {valore ? (
        <div className="mt-1 border border-slate-200 rounded bg-white relative">
          <img src={valore} alt={etichetta} className="w-full h-24 object-contain" />
          <div className="absolute top-1.5 right-1.5 flex gap-1">
            <Button type="button" variant="outline" size="sm" onClick={() => setAperto(true)}>
              <Pen className="w-3 h-3 mr-1" />Rifai
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => onChange(null)}>
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setAperto(true)} data-testid="apri-firma"
                className="mt-1 w-full h-24 border-2 border-dashed border-slate-300 rounded
                           flex flex-col items-center justify-center gap-1 text-slate-500
                           hover:border-orange-400 hover:text-orange-600 transition-colors">
          <Pen className="w-5 h-5" />
          <span className="text-sm font-medium">Tocca per firmare</span>
          <span className="text-[11px]">si apre a tutto schermo</span>
        </button>
      )}

      {aperto && (
        <TelaFirma
          etichetta={etichetta}
          onAnnulla={() => setAperto(false)}
          onConferma={(dataUrl) => { onChange(dataUrl); setAperto(false); }}
        />
      )}
    </div>
  );
}

function TelaFirma({ etichetta, onAnnulla, onConferma }) {
  const contenitoreRef = useRef(null);
  const canvasRef = useRef(null);
  const tratti = useRef([]);          // [[{x,y}, ...], ...] in punti CSS
  const trattoAttivo = useRef(null);
  const puntatoreRef = useRef(null);  // si segue un dito solo: il palmo non deve disegnare
  const [vuoto, setVuoto] = useState(true);

  const ridisegna = useCallback(() => {
    const canvas = canvasRef.current;
    const contenitore = contenitoreRef.current;
    if (!canvas || !contenitore) return;
    const rect = contenitore.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    // La tela ha i pixel veri dello schermo, ma si disegna in punti CSS.
    if (canvas.width !== Math.round(rect.width * dpr) ||
        canvas.height !== Math.round(rect.height * dpr)) {
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    }
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = "#333";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const tratto of tratti.current) {
      if (tratto.length === 1) {
        // Un tocco secco e' un punto: senza questo, il puntino non comparirebbe.
        ctx.beginPath();
        ctx.arc(tratto[0].x, tratto[0].y, ctx.lineWidth / 2, 0, Math.PI * 2);
        ctx.fillStyle = "#333";
        ctx.fill();
        continue;
      }
      ctx.beginPath();
      ctx.moveTo(tratto[0].x, tratto[0].y);
      // Curve fra i punti di mezzo: il tratto viene morbido invece che spezzato.
      for (let i = 1; i < tratto.length - 1; i += 1) {
        const mx = (tratto[i].x + tratto[i + 1].x) / 2;
        const my = (tratto[i].y + tratto[i + 1].y) / 2;
        ctx.quadraticCurveTo(tratto[i].x, tratto[i].y, mx, my);
      }
      const ultimo = tratto[tratto.length - 1];
      ctx.lineTo(ultimo.x, ultimo.y);
      ctx.stroke();
    }
  }, []);

  // Girare il telefono e' il gesto naturale per avere piu' spazio: la firma
  // deve sopravvivere alla rotazione.
  useEffect(() => {
    ridisegna();
    const osservatore = new ResizeObserver(ridisegna);
    if (contenitoreRef.current) osservatore.observe(contenitoreRef.current);
    window.addEventListener("orientationchange", ridisegna);
    return () => {
      osservatore.disconnect();
      window.removeEventListener("orientationchange", ridisegna);
    };
  }, [ridisegna]);

  const puntoDa = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const giu = (e) => {
    if (puntatoreRef.current !== null) return;
    puntatoreRef.current = e.pointerId;
    // La cattura tiene il tratto agganciato al dito anche se esce dalla tela.
    // Non tutti i browser la concedono, e non e' indispensabile: se rifiuta si
    // continua comunque a disegnare.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* pazienza */ }
    trattoAttivo.current = [puntoDa(e)];
    tratti.current.push(trattoAttivo.current);
    setVuoto(false);
    ridisegna();
  };

  const muovi = (e) => {
    if (e.pointerId !== puntatoreRef.current || !trattoAttivo.current) return;
    // Su Android arrivano piu' posizioni per fotogramma: prenderle tutte rende
    // il tratto fedele anche quando si firma in fretta.
    const eventi = typeof e.nativeEvent.getCoalescedEvents === "function"
      ? e.nativeEvent.getCoalescedEvents() : [e.nativeEvent];
    for (const ev of (eventi.length ? eventi : [e.nativeEvent])) {
      trattoAttivo.current.push(puntoDa(ev));
    }
    ridisegna();
  };

  const su = (e) => {
    if (e.pointerId !== puntatoreRef.current) return;
    puntatoreRef.current = null;
    trattoAttivo.current = null;
  };

  // Rete di sicurezza: se il dito si stacca fuori dalla tela e la cattura non
  // era stata concessa, il "su" sulla tela non arriva mai. Senza questo il
  // tratto resterebbe aperto e da li' in poi non si disegnerebbe piu'.
  useEffect(() => {
    const rilascia = () => { puntatoreRef.current = null; trattoAttivo.current = null; };
    window.addEventListener("pointerup", rilascia);
    window.addEventListener("pointercancel", rilascia);
    return () => {
      window.removeEventListener("pointerup", rilascia);
      window.removeEventListener("pointercancel", rilascia);
    };
  }, []);

  const pulisci = () => {
    tratti.current = [];
    trattoAttivo.current = null;
    setVuoto(true);
    ridisegna();
  };

  const annullaTratto = () => {
    tratti.current.pop();
    setVuoto(tratti.current.length === 0);
    ridisegna();
  };

  const conferma = () => {
    if (vuoto) return;
    onConferma(canvasRef.current.toDataURL("image/png"));
  };

  // Il pannello e' a sua volta una finestra di dialogo, non un riquadro fisso
  // appeso al body. Quattro firme su cinque si aprono da dentro un dialogo, e
  // Radix mentre e' aperto mette "pointer-events: none" sul body riaccendendoli
  // solo dentro il proprio contenuto: un pannello appeso al body si vedeva ma
  // non riceveva un tocco. Passando dal dialogo entra nella pila dei livelli e
  // i tocchi arrivano; per giunta Radix lo porta lui fuori dal translate che
  // centra la finestra sotto, che altrimenti lo rimpicciolirebbe.
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onAnnulla(); }}>
      {/* height: 100dvh esclude la barra del browser sul telefono; i browser
          vecchi non la conoscono e allora vale h-screen della classe.

          pointerEvents forzato a "auto": quando questo pannello si apre da
          dentro un'altra finestra, quella di sotto ha gia' spento i tocchi su
          tutto il body. Radix dovrebbe riaccenderli sul livello piu' alto, ma
          qui non stava succedendo. Lo stile in linea passa dopo quello di
          Radix, quindi vince comunque.

          z-[60] perche' le finestre stanno tutte a z-50: senza, restare sopra
          dipende solo dall'ordine in cui finiscono nel documento. */}
      <DialogContent
        data-testid="tela-firma"
        style={{ height: "100dvh", pointerEvents: "auto" }}
        className="max-w-none w-screen h-screen z-[60] rounded-none sm:rounded-none
                   border-0 p-3 gap-3 flex flex-col bg-slate-900/95"
      >
      <DialogHeader className="flex-shrink-0 space-y-0">
        <DialogTitle className="text-white text-base">{etichetta}</DialogTitle>
        <div className="text-xs text-slate-300 text-left">
          Firma con il dito. Gira il telefono se ti serve piu' spazio.
        </div>
      </DialogHeader>

      <div ref={contenitoreRef} className="flex-1 min-h-0 bg-white rounded-lg relative overflow-hidden">
        <canvas ref={canvasRef} className="block w-full h-full"
                style={{ touchAction: "none" }}
                onPointerDown={giu} onPointerMove={muovi}
                onPointerUp={su} onPointerCancel={su} />
        {vuoto && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-slate-300 text-lg">firma qui</span>
          </div>
        )}
        <div className="absolute bottom-3 left-6 right-6 border-b border-dashed border-slate-300 pointer-events-none" />
      </div>

      <div className="flex gap-2 flex-shrink-0">
        <Button type="button" variant="outline" onClick={annullaTratto} disabled={vuoto}
                className="bg-transparent text-white border-slate-500 hover:bg-slate-800">
          <Undo2 className="w-4 h-4 mr-1" />Indietro
        </Button>
        <Button type="button" variant="outline" onClick={pulisci} disabled={vuoto}
                className="bg-transparent text-white border-slate-500 hover:bg-slate-800">
          <RotateCcw className="w-4 h-4 mr-1" />Cancella
        </Button>
        <Button type="button" onClick={conferma} disabled={vuoto}
                className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="conferma-firma">
          <Check className="w-4 h-4 mr-2" />Conferma firma
        </Button>
      </div>
      </DialogContent>
    </Dialog>
  );
}
